<template>
  <div class="frame" :style="themeStyle">
    <MyNav />
    <main class="list-main">
      <div class="list-card">
        <div class="list-header">
          <div class="list-heading">
            <div class="list-title">
              <span class="list-title-icon">
                <i v-if="projectIconClass" :class="projectIconClass"></i>
                <template v-else>{{ projectLetter }}</template>
              </span>
              <h1>{{ displayTitle }}</h1>
            </div>
            <div class="sub">{{ displaySubtitle }}</div>
          </div>
          <div class="header-actions">
            <WeekNavigator
              :week-start="weekStart"
              @change-week="onChangeWeek"
              @go-today="onGoToday"
              @jump-to-date="onJumpToDate"
            />
            <button class="settings-btn" @click="toggleSetting">折叠设置</button>
            <div v-if="settingOpen" class="settings-panel">
              <div class="settings-title">选择可折叠行</div>
              <div class="settings-list">
                <label
                  v-for="(slot, idx) in slots"
                  :key="idx"
                  class="settings-item"
                >
                  <input
                    type="checkbox"
                    :checked="foldableRows.has(idx)"
                    @change="toggleFoldable(idx)"
                  >
                  <span>{{ slot }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <CategoryLegend
          :custom-categories="customCats"
          :comment-unread="commentUnread"
          :like-unread="likeUnread"
          :share-unread="shareUnread"
          :refreshing="refreshing"
          @refresh="onRefresh"
          @add-category="onAddCategory"
          @update-category="onUpdateCategory"
          @delete-category="onDeleteCategory"
          @download="onDownloadTable"
          @fullscreen="onToggleFullscreen"
          @open-share="onOpenShare"
          @open-notifications="onOpenNotifications"
        />

        <TimeTable
          ref="timeTable"
          :days="data.days"
          :events="data.events"
          :foldable-rows="foldableRows"
          :collapsed-rows="collapsedRows"
          :categories="allCategories"
          :project-id="projectId"
          :interactions="interactions"
          commentable
          :active-block="activeBlockForTable"
          @add-event="onAddEvent"
          @update-event="onUpdateEvent"
          @update-status="onUpdateStatus"
          @delete-event="onDeleteEvent"
          @toggle-collapse="toggleCollapse"
          @hover-event="onHoverEvent"
          @hover-leave="onHoverLeave"
          @table-scroll="onTableScroll"
        />

        <TimeStats
          :days="data.days"
          :events="data.events"
          :categories="allCategories"
        />

        <!-- 时间块评论气泡：鼠标悬停时间块时浮出，显示该块的点赞数与评论，可回复好友的评论 -->
        <BlockBubble
          ref="bubble"
          :visible="bubbleVisible"
          :block="bubbleBlock"
          :anchor="bubbleAnchor"
          :pinned="bubblePinned"
          :pinnable="false"
          resource-type="TIMEBLOCK"
          :title="bubbleBlock ? bubbleBlock.text : ''"
          :subtitle="bubbleBlock ? bubbleBlock.timeLabel : ''"
          @pointer-enter="onBubblePointerEnter"
          @pointer-leave="onBubblePointerLeave"
          @interacting="onBubbleInteracting"
          @changed="onBubbleChanged"
          @close="hideBubble"
        />

        <!-- 开放时间表权限弹窗：选择对哪些好友开放（只读 / 编辑） -->
        <ShareDialog
          :visible="shareVisible"
          :project-id="projectId"
          :title="projectName"
          @close="shareVisible = false"
          @changed="refreshNotificationCounts"
        />

        <!-- 删除分类时，若该分类已被时间块使用：让用户选择「删除时间块」还是「转移到其他分类」 -->
        <CategoryDeleteDialog
          :visible.sync="deleteCatVisible"
          :category="deleteCatTarget"
          :block-count="deleteCatBlockCount"
          :target-options="deleteCatTargets"
          @confirm="onConfirmDeleteCategory"
        />

        <!-- 评论 / 点赞 提醒面板 -->
        <NotificationPanel
          :visible="notifVisible"
          :type="notifType"
          @close="notifVisible = false"
          @read="refreshNotificationCounts"
        />
      </div>
    </main>
  </div>
</template>

<script>
import MyNav from '@/components/home/MyNav.vue'
import WeekNavigator from './WeekNavigator.vue'
import CategoryLegend from './CategoryLegend.vue'
import TimeTable from './TimeTable.vue'
import TimeStats from './TimeStats.vue'
import ShareDialog from './ShareDialog.vue'
import NotificationPanel from './NotificationPanel.vue'
import CategoryDeleteDialog from './CategoryDeleteDialog.vue'
import BlockBubble from '@/components/taskblock/friend/BlockBubble.vue'
import { createTag, listTagsByProject, updateTag, deleteTag, getTagBlockCount, removeTagWithBlocks } from '@/api/tag.js'
import { getTimeBlocksRange, getOwnActivity } from '@/api/timeline.js'
import { listNotifications, getProjectVersion } from '@/api/share.js'
import { getInteractionSummary } from '@/api/comment.js'
import {
  restoreTimers,
  scheduleTimer,
  cancelTimer
} from '@/utils/runningTimer.js'
import { runAchievementCheck } from '@/components/achievement/achievementStore.js'
import {
  SLOTS,
  buildTagName,
  parseTagName,
  findDefaultPreset,
  normalizeHexColor,
  minutesToLabel,
  nextBlockKey,
  DAY_MINUTES
} from './constants'

/**
 * 把「开始/结束时间」映射到当天的分钟坐标（0 ~ 1440），供表格定位使用。
 *
 * ⚠️ 不能只取 getHours()/getMinutes()：记录可能是**跨午夜**的
 * （例如 20:57 → 次日 00:00），那样结束时间会被算成 0 分，于是 end < start ——
 * 既让时间块在表格里渲染不出来（切片条件 e > s 永不成立），
 * 又会让分类统计出现负时长、把「合计」拖小。
 * 因此跨天时把结束时间统一收敛到当天 24:00（= 1440）。
 */
function toDayRange(startStr, endStr) {
  const s = new Date(startStr)
  if (!startStr || isNaN(s.getTime())) return { start: 0, end: 0 }
  const start = s.getHours() * 60 + s.getMinutes()
  const e = new Date(endStr)
  if (!endStr || isNaN(e.getTime())) return { start, end: start }
  const sameDay = e.getFullYear() === s.getFullYear() &&
                  e.getMonth() === s.getMonth() &&
                  e.getDate() === s.getDate()
  let end = sameDay ? e.getHours() * 60 + e.getMinutes() : 24 * 60
  if (end < start) end = start
  return { start, end }
}

/** 记录的真实时长（分钟）：按完整时间戳相减，跨午夜也正确。 */
function durationMinutes(startStr, endStr) {
  const s = new Date(startStr)
  const e = new Date(endStr)
  if (!startStr || !endStr || isNaN(s.getTime()) || isNaN(e.getTime())) return 0
  return Math.max(0, Math.round((e.getTime() - s.getTime()) / 60000))
}

function getMonday(d) {
  const dt = new Date(d)
  const day = dt.getDay()
  const diff = (day === 0 ? -6 : 1) - day
  dt.setDate(dt.getDate() + diff)
  dt.setHours(0, 0, 0, 0)
  return dt
}

function fmtDate(d) {
  const m = (d.getMonth() + 1).toString().padStart(2, '0')
  const day = d.getDate().toString().padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function emptyData(monday) {
  const days = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday)
    d.setDate(d.getDate() + i)
    days.push({ date: fmtDate(d) })
  }
  return { days, events: [[], [], [], [], [], [], []] }
}

export default {
  name: 'ListPage',
  components: {
    MyNav,
    WeekNavigator,
    CategoryLegend,
    TimeTable,
    TimeStats,
    ShareDialog,
    NotificationPanel,
    CategoryDeleteDialog,
    BlockBubble
  },
  props: {
    workspaceId: {
      type: [Number, String],
      default: null
    },
    projectId: {
      type: [Number, String],
      default: null
    },
    projectName: {
      type: String,
      default: ''
    },
    projectDescription: {
      type: String,
      default: ''
    },
    /**
     * 当前工作表的主色（hex）。目前**不参与渲染**：标题左侧的图标已改为跟随标题字体色。
     * 保留该 prop 是因为「覆盖 --lp-* 令牌」由父组件 themeStyle 完成，色板 hex 仍在这里透传备查。
     */
    projectColor: {
      type: String,
      default: ''
    },
    /**
     * 当前工作表的图标类（如 'fas fa-clock'）。有值就渲染图标，
     * 没有（工作表未选预设图标）则回退显示 projectLetter。
     * 由 TaskBlockPage 从左侧工作区列表的数据透传过来，保证两处图标一致。
     */
    projectIconClass: {
      type: String,
      default: ''
    },
    /** 工作表名称首字母，作为无预设图标时的兜底显示。 */
    projectLetter: {
      type: String,
      default: ''
    },
    /**
     * 一组 CSS 变量对象（如 {'--lp-primary': '#5DB7F5', ...}），
     * 由父组件根据工作表的 hex 派生。挂在 .frame 根元素上后，
     * 子组件的 `var(--lp-*, 兜底值)` 自动跟随工作表配色。
     */
    themeStyle: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      weekStart: getMonday(new Date()),
      data: emptyData(getMonday(new Date())),
      saveTimer: null,
      slots: SLOTS,
      settingOpen: false,
      foldableRows: new Set(),
      collapsedRows: new Set(),
      customCats: [],
      // ===== 删除分类时的时间块处置（分类被时间块引用时弹选择框） =====
      deleteCatVisible: false,
      /** 正在删除的分类 { key, label, color } */
      deleteCatTarget: null,
      /** 该分类在本周被多少个时间块使用 */
      deleteCatBlockCount: 0,
      /** 可转移的目标分类（已排除被删分类本身） */
      deleteCatTargets: [],
      /** 待删除分类的 key（选择框确认后据此执行） */
      deleteCatPendingKey: '',
      // ===== 开放 / 评论 / 点赞 互动 =====
      shareVisible: false,
      notifVisible: false,
      notifType: 'COMMENT',
      // 评论 / 点赞 未读数（驱动「评论」「喜欢」按钮的红点角标）
      commentUnread: 0,
      likeUnread: 0,
      // 分享类未读（好友开放 / 调整 / 收回时间表权限）
      shareUnread: 0,
      /** 手动刷新进行中：驱动图例右上角刷新按钮的旋转 / 禁用态 */
      refreshing: false,
      // ===== 跨端同步（版本号轮询）=====
      // 上次全量加载拿到的版本号，作为轮询基线；变化时静默重拉当前周。
      version: null,
      versionTimer: null,
      /**
       * 提醒轮询定时器：好友评论 / 点赞发生在他的浏览器里，
       * 本页不轮询的话要手动刷新才能看到红点（用户反馈过"评论了却没提醒"）。
       */
      notifTimer: null,
      // ===== 时间块互动角标：blockId → { commentCount, likeCount } =====
      // 由 loadInteractions() 一次性拉取整表的互动统计，供 TimeTable 在块右下角画角标
      interactions: {},
      /** 互动统计请求令牌：快速切周时丢弃过期响应，避免旧结果覆盖新结果 */
      interactionsToken: 0,
      // ===== 时间块评论气泡（悬停时间块浮出，显示点赞数 + 评论，可回复） =====
      bubbleVisible: false,
      bubbleBlock: null,
      /** 源时间块的屏幕坐标，气泡按它贴在右侧 */
      bubbleAnchor: null,
      /** 是否已固定（鼠标移入气泡）：固定后不再自动消失 */
      bubblePinned: false,
      /** 鼠标是否停在气泡里（停留期间不自动消失） */
      pointerInBubble: false,
      /** 当前气泡对应的源时间块坐标，供 TimeTable 高亮 */
      bubbleDayIdx: -1,
      bubbleEventIndex: -1,
      /** 源时间块的 key：下标会因记录增删而错位，高亮优先认 key */
      bubbleEventKey: null,
      // 悬停 0.5s 才弹 / 4s 后自动淡出 / 移开后 240ms 收起
      hoverTimer: null,
      autoHideTimer: null,
      leaveTimer: null,
      /**
       * 周数据请求的**序列号**：每次 loadWeek 自增。
       * 作用：丢弃「被取代的」过期响应 —— 例如快速切周、或本次请求发出后用户又发起了新请求，
       * 旧的响应晚到时若还按它覆盖 this.data，就会把当前正在看的周数据冲掉（表现为"刚改的块没了/回来了"）。
       * 注意：这只挡"被取代"的响应；若本次就是最新请求、但用户在其进行期间本地改过块，
       * 仍需配合 weekLocalEdits 的合并逻辑（见 applyWeekData），否则仍会覆盖本地改动。
       */
      loadSeq: 0,
      /**
       * 本地改动追踪：'<weekKey>' -> Set(dayIdx)。
       * 记录「本次周数据加载发出之后、响应回来之前」用户对哪些天做过增删改。
       * 响应回来时，这些天不能简单地被服务端数据整段替换（否则刚创建/修改的块会被冲掉），
       * 而要用 applyWeekData 按 id 合并：本地块优先、服务端独有的块补回，既不复读也不丢。
       */
      weekLocalEdits: {},
      justEdited: false
    }
  },
  computed: {
    allCategories() {
      return this.customCats
    },
    displayTitle() {
      return this.projectName || 'Hi! 时间记录表'
    },
    displaySubtitle() {
      return this.projectDescription || '想都是问题，做才是答案'
    },
    /** 当前评论气泡指向的源时间块坐标，传给 TimeTable 做高亮；气泡没开时返回 null */
    activeBlockForTable() {
      if (!this.bubbleVisible || this.bubbleDayIdx < 0 || this.bubbleEventIndex < 0) return null
      return { dayIdx: this.bubbleDayIdx, index: this.bubbleEventIndex, key: this.bubbleEventKey }
    }
  },
  watch: {
    weekStart: {
      immediate: true,
      handler() {
        this.loadWeek(this.weekStart)
      }
    },
    projectId: {
      immediate: true,
      handler() {
        this.loadTags()
        this.refreshNotificationCounts()
      }
    }
  },
  created() {
    this.loadTags()
    this.refreshNotificationCounts()
    // 恢复「在做」的定时提醒：定时按时间块 id 存 localStorage，
    // 刷新页面后继续有效（已过期的直接丢弃，不补提醒，见 runningTimer.restoreTimers）。
    // ⚠️ 幂等：切工作表会让本组件带着新 key 重挂，restoreTimers 内部按 id 覆盖式重排，不会重复。
    restoreTimers()
  },
  mounted() {
    // 「在做」的定时弹窗可能在 TaskBlockPage 里点了「结束」，那边已调后端改状态，
    // 这里收 window 事件把本地那一天的那一条重新派生（不整周重拉）。
    window.addEventListener('taskblock:running-ended', this.onRunningEnded)
    // 跨天结束时的「第二天那块」：定时弹窗里结束会广播过来，补到对应日期的列里
    window.addEventListener('taskblock:running-split', this.onRunningSplit)
    // 每 60s 静默拉一次未读数：好友在他自己的设备上评论 / 点赞后，
    // 本页无需刷新即可亮起红点（提醒面板打开时会自行拉取并标记已读，这里只更红点）
    this.notifTimer = setInterval(() => {
      if (this.notifVisible) return
      this.refreshNotificationCounts()
    }, 60000)
    // 跨端同步：打开工作表期间轮询版本号，其他端改表后自动刷新（与好友页一致）
    this.startVersionPoll()
  },
  beforeDestroy() {
    window.removeEventListener('taskblock:running-ended', this.onRunningEnded)
    window.removeEventListener('taskblock:running-split', this.onRunningSplit)
    // 离开表格页时清掉悬停/自动收起计时器，避免组件销毁后计时器仍在跑
    this.clearTimers()
    this.stopVersionPoll()
    if (this.notifTimer) {
      clearInterval(this.notifTimer)
      this.notifTimer = null
    }
  },
  methods: {
    storagePrefix() {
      return this.projectId ? `project-${this.projectId}-` : ''
    },
    weekKey(d) {
      return this.storagePrefix() + 'week-v2-' + fmtDate(d)
    },
    customCatsKey() {
      return this.storagePrefix() + 'list-custom-categories'
    },
    async loadWeek(monday) {
      // ⚠️ 每次加载自增序列号，用于丢弃「被取代的」过期响应（见 data.loadSeq 注释）。
      // 同时清空本周的本地改动记录：新一轮加载以服务端/缓存为准的起点重新计数，
      // 用户在本轮响应回来前做的改动会在 applyWeekData 里被合并回来，不会丢。
      const seq = ++this.loadSeq
      this.weekLocalEdits = { ...this.weekLocalEdits, [this.weekKey(monday)]: new Set() }
      const workspaceId = this.workspaceId || localStorage.getItem('taskblock-current-workspace-id')
      if (workspaceId && this.projectId) {
        try {
          const week = await this.fetchWeekFromApi(monday, workspaceId)
          if (seq !== this.loadSeq) return
          this.applyWeekData(monday, week)
          this.applyOwnStatus(week)
          this.loadInteractions()
          return
        } catch (e) {
          console.warn('[ListPage] API 加载周数据失败，回退到本地缓存', e)
          if (seq !== this.loadSeq) return
          // 落到本地缓存兜底（同一轮请求，seq 仍有效）
        }
      }
      const week = this.readWeekFromStorage(monday)
      if (seq !== this.loadSeq) return
      this.applyWeekData(monday, week)
      // 本地缓存回退时也拉一次互动统计：缓存里的 evt.id 仍可用来取角标
      this.loadInteractions()
    },

    /**
     * 把「版本号基线 + 自己的实时状态」从区间响应里落下来，并把状态上抛给父级（TaskBlockPage）
     * 渲染左下角动态行。口径与好友页方案3 完全一致。
     *
     * @param {Object} week fetchWeekFromApi 的返回：{ days, events, version, activity }
     */
    applyOwnStatus(week) {
      if (week && week.version != null) {
        this.version = week.version
      }
      this.$emit('activity-changed', (week && week.activity) || null)
    },

    /**
     * 后台静默重拉当前周（**仅由版本号变化触发**，即其他端改动了本表）。
     *
     * 与 loadWeek 的区别：
     * - **服务端权威、整段替换**：本方法的响应由「版本号变化」触发，是权威快照，
     *   不能再走 applyWeekData 的「本地块优先」合并 —— 否则本地同 id 的旧副本会把
     *   其他端的更新（结束在做 RUNNING→COMPLETED、改内容/时间、删除）按 id 遮蔽，
     *   表现为「左下角实时状态变了、但时间表里的块没变」。
     *   （各写入入口都是「先调接口成功再 emit 本地变更」，故本地改动此时均已落库。）
     * - **不自增 loadSeq** —— 不打断用户主动发起的加载；
     * - **拉取期间用户又改本地（justEdited）→ 放弃本轮**，且不推进基线，下一轮重试；
     * - 期间若用户切了周或发起了新的加载（weekStart / loadSeq 变化）→ 丢弃本次结果。
     */
    async backgroundReload() {
      const monday = this.weekStart
      const seqAtStart = this.loadSeq
      const workspaceId = this.workspaceId || localStorage.getItem('taskblock-current-workspace-id')
      if (!workspaceId || !this.projectId) return
      try {
        const week = await this.fetchWeekFromApi(monday, workspaceId)
        if (monday !== this.weekStart || seqAtStart !== this.loadSeq) return
        // 拉取期间用户又做了本地改动 → 本轮不替换（避免冲掉在途/刚落库的编辑），
        // 也不推进基线（不调 applyOwnStatus），下一轮轮询会重试。
        if (this.justEdited) return
        // 跨端同步：整段替换，服务端为准。
        this.data = { days: week.days, events: week.events }
        // 本地改动均已落库，服务端即最新；清掉本周本地改动标记，
        // 防止残留标记继续遮蔽后续同步。
        this.weekLocalEdits[this.weekKey(monday)] = new Set()
        this.applyOwnStatus(week) // 顺带把基线推进到本次响应的 version
        this.loadInteractions()
      } catch (e) {
        // 静默失败，下个周期再试（不打扰用户）
      }
    },

    /**
     * 轻量刷新底部「自己的实时状态」行动态行（不重拉整张表）。
     *
     * <p>用于本地编辑后跳过整表静默重拉时，仍让动态行保持新鲜：只拉本人动态这一个字段，
     * 避免把本地改动牵扯进整表合并，也更省。失败静默忽略，下个轮询周期再试。</p>
     */
    async refreshOwnActivity() {
      try {
        const res = await getOwnActivity()
        if (res && res.success && res.code === 200) {
          this.$emit('activity-changed', (res.data) || null)
        }
      } catch (e) {
        // 失败静默忽略，下个周期再试
      }
    },

    /** 开始（或重启）版本号轮询。同一时刻只保留一个计时器。 */
    startVersionPoll() {
      this.stopVersionPoll()
      if (!this.projectId) return
      this.versionTimer = setInterval(this.checkVersion, 20000)
    },

    /** 停止版本号轮询。 */
    stopVersionPoll() {
      if (this.versionTimer) {
        clearInterval(this.versionTimer)
        this.versionTimer = null
      }
    },

    /**
     * 轮询版本号：只取一个整数与基线比较。
     * 变化 → 说明其他端改动了这张工作表 → 静默重拉（时间表 + 左下角动态行一并刷新）。
     * 首次拿到基线（version 为 null）时只记录、不触发重拉。
     */
    async checkVersion() {
      if (!this.projectId) return
      try {
        const res = await getProjectVersion(this.projectId)
        if (!res || !res.success || res.code !== 200) return
        const latest = res.data ? res.data.version : null
        if (latest == null) return
        if (this.version == null) {
          this.version = latest
          return
        }
        if (String(latest) !== String(this.version)) {
          if (this.justEdited) {
            // 本地刚改过：本轮不整表重拉，只刷新底部动态行；且**刻意不推进基线** ——
            // 这样「编辑停下来之后的下一轮轮询」会正常整表重拉，既能合并连续编辑期间的多次重拉，
            // 又能兜住并发/他端改动（若在跳过时就推进基线，会把这些改动永久漏掉）。
            this.justEdited = false
            await this.refreshOwnActivity()
          } else {
            // 不在调用前推进基线：由 backgroundReload 成功应用服务端快照后再推进。
            // 若拉取期间用户又改了本地（justEdited 变 true）会放弃本轮、基线不动 → 下一轮重试；
            // 也避免「请求失败却已推进基线」导致他端改动被永久漏掉。
            await this.backgroundReload()
          }
        }
      } catch (e) {
        // 轮询失败静默忽略，下个周期再试
      }
    },

    /**
     * 把一周数据应用到 this.data，但**保护本地改动不被整段冲掉**。
     *
     * <p>背景：用户先创建了块 A、又改了 A（此时 this.data 已是本地最新），
     * 若此刻一个「晚到/过期」的周加载响应（服务端还停在 A 的旧状态）直接 `this.data = ...`，
     * A 就会被冲回修改前 —— 这正是「A 回到修改前的状态，并和新创建的 B 冲突」的根因。</p>
     *
     * <p>策略：</p>
     * - 本周没有任何本地改动的天 → 直接采用服务端/缓存数据（普通加载，行为不变）；
     * - 有本地改动的天（weekLocalEdits 记录）→ 按**服务端 id 合并**：本地块优先（带回来用户刚改的值），
     *   服务端独有（用户没动过、或本次才从库里查出来的）块再补回，既不重复、也不丢。</p>
     */
    applyWeekData(monday, weekData) {
      const wk = this.weekKey(monday)
      const edits = this.weekLocalEdits[wk]
      if (!edits || edits.size === 0) {
        // 只取 days/events：weekData 可能还带 version/activity（跨端同步用），不混进 data
        this.data = { days: weekData.days, events: weekData.events }
        return
      }
      const serverEvents = weekData.events || [[], [], [], [], [], [], []]
      const events = serverEvents.map((serverDay, i) => {
        if (!edits.has(i)) return serverDay
        // 本地这一天：用户刚创建/修改/删除过的块，以它为准
        const localDay = (this.data.events[i] || []).slice()
        const seenIds = new Set()
        const merged = []
        for (const e of localDay) {
          merged.push(e)
          if (e.id) seenIds.add(String(e.id))
        }
        // 服务端这一天里、本地没有（按 id 去重）的块补回来，避免把用户没动过的旧块丢掉
        for (const e of (serverDay || [])) {
          if (e.id && seenIds.has(String(e.id))) continue
          merged.push(e)
        }
        return merged
      })
      this.data = { days: weekData.days, events }
    },
    readWeekFromStorage(monday) {
      const key = this.weekKey(monday)
      try {
        const raw = localStorage.getItem(key)
        if (raw) {
          const parsed = JSON.parse(raw)
          // 迁移旧版 work 分类到 study；顺便给没有 key 的历史记录补上稳定标识
          // （key 是后来才加的，老缓存里的块一旦改动就会因为下标失效而"改不动"）
          if (parsed.events) {
            parsed.events.forEach(dayEvents => {
              (dayEvents || []).forEach(evt => {
                if (evt.cat === 'work') {
                  evt.cat = 'study'
                }
                if (!evt.key) {
                  evt.key = nextBlockKey()
                }
              })
            })
          }
          return parsed
        }
      } catch (e) {
        // ignore storage errors
      }
      return emptyData(monday)
    },
    async fetchWeekFromApi(monday, workspaceId) {
      const end = new Date(monday)
      end.setDate(end.getDate() + 6)
      const params = {
        workspaceId,
        projectId: this.projectId,
        startDate: fmtDate(monday),
        endDate: fmtDate(end)
      }
      console.log('[ListPage] 请求 timeline/range:', params)
      const res = await getTimeBlocksRange(params)
      console.log('[ListPage] timeline/range 返回:', res)
      if (!res.success || res.code !== 200) {
        throw new Error(res.message || '查询时间记录失败')
      }
      const items = res.data && res.data.items ? res.data.items : {}
      const days = []
      const events = [[], [], [], [], [], [], []]
      for (let i = 0; i < 7; i++) {
        const d = new Date(monday)
        d.setDate(d.getDate() + i)
        const dateStr = fmtDate(d)
        days.push({ date: dateStr })
        events[i] = (items[dateStr] || []).map(item => this.convertItemToEvent(item))
      }
      // 区间响应已附带版本号（跨端同步基线）与自己的实时状态（方案3）
      return {
        days,
        events,
        version: res.data ? res.data.version : null,
        activity: res.data ? res.data.activity : null
      }
    },
    /**
     * 批量拉取本表所有时间块的互动统计（评论数 / 点赞数），供时间块右下角角标使用。
     *
     * <p>一次请求覆盖整表，绝不按块逐个请求；失败静默 —— 角标只是锦上添花，不能影响看表。
     * 用 token 丢弃过期响应：快速切周时先发的请求可能后到，不能让它覆盖新一周的结果。</p>
     */
    async loadInteractions() {
      const ids = [...new Set(
        (this.data.events || [])
          .reduce((all, dayEvents) => all.concat(dayEvents || []), [])
          .map(evt => evt && evt.id)
          .filter(Boolean)
      )]
      const token = ++this.interactionsToken
      if (!ids.length) {
        this.interactions = {}
        return
      }
      try {
        const res = await getInteractionSummary({ resourceType: 'TIMEBLOCK', resourceIds: ids })
        if (token !== this.interactionsToken) return
        if (res && res.success && res.code === 200) {
          this.interactions = res.data || {}
        }
      } catch (e) {
        console.warn('[ListPage] 拉取时间块互动统计失败', e)
      }
    },
    convertItemToEvent(item) {
      const cat = item.tagId ? String(item.tagId) : 'none'
      const derived = this.deriveEventFields(item)
      return {
        id: item.id,
        // 稳定标识：增删改都按它定位（见 constants.nextBlockKey 的说明）
        key: nextBlockKey(),
        cat,
        text: item.content || '',
        color: item.color || '',
        // 原始时间戳一并带上：状态流转（预设→完成 / 正在做→结束）后，
        // 需要据此重新派生 start/end/kind，否则本地数据不会跟着变。
        actualStartTime: item.actualStartTime || null,
        actualEndTime: item.actualEndTime || null,
        plannedStartTime: item.plannedStartTime || null,
        plannedEndTime: item.plannedEndTime || null,
        ...derived
      }
    },
    /**
     * 从原始时间戳派生渲染所需字段，并推断时间块类型（kind）：
     * - 实际开始 + 实际结束都有 → 'done'（普通已完成）
     * - 只有实际开始 → 'running'（正在做，填充「从开始到下一个时间块」这一段）
     * - 都没有 → 'planned'（预设）
     *
     * ⚠️ 渲染层完全基于 actual 字段推断，不依赖后端 executionStatus 字符串，
     *    这样旧数据（状态写的是 PLANNED 但 actual 已填）也能正确识别成普通块。
     */
    deriveEventFields(item) {
      const hasActualStart = !!item.actualStartTime
      const hasActualEnd = !!item.actualEndTime
      let kind = 'done'
      if (!hasActualStart && !hasActualEnd) kind = 'planned'
      else if (hasActualStart && !hasActualEnd) kind = 'running'

      const startStr = item.actualStartTime || item.plannedStartTime
      const endStr = item.actualEndTime || item.plannedEndTime
      const { start, end } = toDayRange(startStr, endStr)
      // 正在做：结束时间还没发生，块要占位到「下一个时间块的前一分钟」——
      // 这个边界在创建时已经写进 plannedEndTime（= 当时所在空档的末尾），直接拿来用即可；
      // 只有老数据（plannedEnd 缺失 / 比开始还早）才退回「铺到当天结束」的旧行为。
      if (kind === 'running') {
        return { start, end: end > start ? end : DAY_MINUTES, duration: 0, kind }
      }
      return {
        start,
        end,
        // 真实时长：按完整时间戳算，跨午夜的记录不会被算成负数
        duration: hasActualStart && hasActualEnd ? durationMinutes(startStr, endStr) : 0,
        kind
      }
    },
    scheduleSave() {
      // 仅作为离线兜底缓存（接口失败时 loadWeekFromStorage 会读它）。
      // 界面上不再显示保存状态文案，所以这里也不再维护 saveState。
      clearTimeout(this.saveTimer)
      this.saveTimer = setTimeout(() => {
        try {
          localStorage.setItem(this.weekKey(this.weekStart), JSON.stringify(this.data))
        } catch (e) {
          // ignore storage errors
        }
      }, 500)
      // 时间块增删改都可能达成新成就（累计学习时长 / 连续记录 / 单日里程碑等），
      // 顺带触发一次成就检测；后端已在此前完成落库，这里只做检测与弹窗
      runAchievementCheck(true)
    },
    loadCustomCats() {
      try {
        const raw = localStorage.getItem(this.customCatsKey())
        if (raw) {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) {
            this.customCats = parsed
          }
        }
      } catch (e) {
        this.customCats = []
      }
    },
    saveCustomCats() {
      try {
        localStorage.setItem(this.customCatsKey(), JSON.stringify(this.customCats))
      } catch (e) {
        // ignore storage errors
      }
    },
    async loadTags() {
      if (!this.projectId) {
        this.loadCustomCats()
        return
      }
      console.log('[ListPage] 加载标签，projectId:', this.projectId)
      try {
        const res = await listTagsByProject(this.projectId)
        console.log('[ListPage] 标签接口返回:', res)
        if (!res.success || res.code !== 200) {
          console.warn('获取分类失败', res.message)
          return
        }
        // ⚠️ 图标**不单独存字段**：'fa:book学习' / '🍕吃饭' 这种「图标+名称」的整串
        // 就存在 name 里（用户要求），由 CategoryLegend 的 parseTagName 拆开渲染。
        // 所以 label 是原始整串，别再拼接。
        this.customCats = (res.data || []).map(tag => ({
          key: String(tag.id),
          label: tag.name,
          color: tag.color
        }))
        console.log('[ListPage] 自定义分类:', this.customCats)
        await this.upgradeDefaultTags()
      } catch (e) {
        console.error('加载分类失败', e)
      }
    },
    /**
     * 四个默认分类的**一次性升级**：补图标 + 换成新配色。
     *
     * 为什么需要：`DEFAULT_CATEGORY_PRESETS` 只影响新建的工作表，
     * 之前建好的那批分类在库里还是「学习」这种没图标、旧配色的记录，光改前端代码看不到效果。
     *
     * 安全边界（避免误伤用户自己的分类）：
     * - 只处理**纯名称完全等于**四个默认名之一的
     * - 只处理**当前没图标**的（已经是 fa/emoji 图标的一律跳过，那是用户自己的选择）
     * - 写完再比对一遍目标值，已达标就跳过 → 幂等，每次进页面最多各写一次
     * - 单个失败只 warn，不影响其它分类
     */
    async upgradeDefaultTags() {
      let changed = false
      for (const cat of this.customCats) {
        const parsed = parseTagName(cat.label)
        if (parsed.iconType) continue
        const preset = findDefaultPreset(cat.label)
        if (!preset) continue
        const targetName = buildTagName('fa', preset.icon, preset.name)
        const targetColor = normalizeHexColor(preset.color)
        // 已经升级过就跳过（幂等）
        if (cat.label === targetName && normalizeHexColor(cat.color) === targetColor) continue
        try {
          const res = await updateTag(cat.key, { name: targetName, color: targetColor, icon: '' })
          if (!res.success || res.code !== 200) {
            console.warn(`[ListPage] 升级默认分类 "${preset.name}" 失败`, res.message)
            continue
          }
          changed = true
        } catch (e) {
          console.warn(`[ListPage] 升级默认分类 "${preset.name}" 异常`, e)
        }
      }
      if (changed) await this.loadTags()
    },
    async onAddCategory({ name, color }) {
      if (!this.projectId) {
        alert('请先选择工作表')
        return
      }
      try {
        // name 里已经含图标前缀（'fa:book学习' / '🍕吃饭'），icon 字段留空不再使用
        const res = await createTag({
          projectId: this.projectId,
          name,
          color,
          icon: ''
        })
        if (!res.success || res.code !== 200) {
          alert(res.message || '创建分类失败')
          return
        }
        await this.loadTags()
      } catch (e) {
        alert('网络异常，请稍后重试')
      }
    },
    async onUpdateCategory({ key, name, color }) {
      if (!this.projectId) {
        // 没选工作表时分类只存在本地，直接改本地数组即可
        this.customCats = this.customCats.map(c => (
          c.key === key ? { ...c, label: name, color } : c
        ))
        this.saveCustomCats()
        return
      }
      try {
        // name 里已经含图标前缀（'fa:book学习' / '🍕吃饭'），icon 字段留空不再使用
        const res = await updateTag(key, { name, color, icon: '' })
        if (!res.success || res.code !== 200) {
          alert(res.message || '修改分类失败')
          return
        }
        await this.loadTags()
      } catch (e) {
        alert('网络异常，请稍后重试')
      }
    },
    /** 分类的纯名称（去掉 'fa:book' / emoji 前缀），只用于提示文案 */
    categoryPlainName(cat) {
      if (!cat || !cat.label) return '该分类'
      return parseTagName(cat.label).text || '该分类'
    },
    /** 收集本周所有使用指定分类的时间块 → [{ dayIdx, index, evt }] */
    collectBlocksByCategory(key) {
      const result = []
      for (let d = 0; d < 7; d++) {
        const events = this.data.events[d] || []
        events.forEach((evt, index) => {
          if (evt && String(evt.cat) === String(key)) result.push({ dayIdx: d, index, evt })
        })
      }
      return result
    },
    /**
     * 删除分类（入口）。
     *
     * 分类若已被时间块使用，**不能直接删** —— 那些块会失去归属。此时弹出选择框，
     * 让用户决定：① 连同这些时间块一起删除；② 把时间块转移到另一个分类。
     * 没被使用时保持原来的轻量确认 + 直接删除。
     */
    async onDeleteCategory(key) {
      const cat = this.customCats.find(c => String(c.key) === String(key))
      const targets = this.customCats.filter(c => String(c.key) !== String(key))

      // 使用数量必须是「全部时间块」——前端只有当前一周，所以有工作表时向后端要总数；
      // 拿不到（离线 / 接口异常）时退回本周统计，至少不会漏弹选择框。
      let total = this.collectBlocksByCategory(key).length
      if (this.projectId) {
        try {
          const res = await getTagBlockCount(key)
          if (res && res.success && res.code === 200) {
            total = Number(res.data) || 0
          }
        } catch (e) {
          console.warn('[ListPage] 查询分类使用次数失败，退回本周统计', e)
        }
      }

      if (total === 0) {
        try {
          await this.$confirm(`确定删除分类「${this.categoryPlainName(cat)}」吗？`, '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          })
        } catch (e) {
          return
        }
        await this.performDeleteCategory(key, { action: 'none' })
        return
      }

      // 被时间块使用 → 打开选择框（不在这里删，等用户选完再执行）
      this.deleteCatTarget = cat || { key, label: '', color: '' }
      this.deleteCatBlockCount = total
      this.deleteCatTargets = targets
      this.deleteCatPendingKey = String(key)
      this.deleteCatVisible = true
    },
    /** 选择框确认：按用户选择执行删除 + 时间块处置 */
    onConfirmDeleteCategory({ action, targetKey }) {
      const key = this.deleteCatPendingKey
      this.deleteCatVisible = false
      if (!key) return
      this.performDeleteCategory(key, { action, targetKey })
    },
    /**
     * 真正执行「删除分类」。
     *
     * 后端一次性处理**全部**（跨周）引用该分类的时间块：
     * - `action === 'none'` → 普通 `deleteTag`（分类没被使用）；
     * - `action === 'delete'` → `POST /tags/{id}/remove` + `DELETE_BLOCKS`；
     * - `action === 'convert'` → `POST /tags/{id}/remove` + `TRANSFER` + `targetTagId`。
     *
     * 前端本地只同步「当前加载的这一周」，其余周由后端改完后，下次加载自然是新分类。
     * 后端调用失败则直接中止，不动本地数据。
     *
     * @param {string} key 被删分类 key
     * @param {{action:'none'|'delete'|'convert', targetKey?:string}} opt
     */
    async performDeleteCategory(key, { action = 'none', targetKey = null } = {}) {
      const targetCat = action === 'convert'
        ? this.customCats.find(c => String(c.key) === String(targetKey))
        : null
      const targetColor = targetCat ? normalizeHexColor(targetCat.color) : null

      // 1) 后端：删分类 + 处置全部时间块（跨周）
      if (this.projectId) {
        try {
          const res = action === 'none'
            ? await deleteTag(key)
            : await removeTagWithBlocks(key, {
              strategy: action === 'delete' ? 'DELETE_BLOCKS' : 'TRANSFER',
              targetTagId: action === 'convert' ? String(targetKey) : null
            })
          if (!res.success || res.code !== 200) {
            alert(res.message || '删除分类失败')
            return
          }
          this.customCats = this.customCats.filter(c => String(c.key) !== String(key))
        } catch (e) {
          alert('网络异常，请稍后重试')
          return
        }
      } else {
        // 无工作表（纯本地）→ 只改本地分类，时间块按当前周处理
        this.customCats = this.customCats.filter(c => String(c.key) !== String(key))
        this.saveCustomCats()
      }

      if (action === 'none') return

      // 2) 本地同步当前周（后端已处理全部，这里只是让界面立刻反映）
      const affected = this.collectBlocksByCategory(key)

      if (action === 'delete') {
        for (const item of affected) {
          if (item.evt.id) cancelTimer(item.evt.id)
        }
        for (let d = 0; d < 7; d++) {
          const events = this.data.events[d] || []
          const kept = events.filter(evt => !evt || String(evt.cat) !== String(key))
          if (kept.length !== events.length) {
            this.$set(this.data.events, d, kept)
            this.markLocalEdit(d)
          }
        }
        this.$set(this.data, 'events', this.data.events.slice())
        this.scheduleSave()
        this.refreshOwnActivity()
        return
      }

      if (action === 'convert') {
        for (const item of affected) {
          this.$set(item.evt, 'cat', targetCat ? String(targetCat.key) : 'none')
          if (targetColor) this.$set(item.evt, 'color', targetColor)
        }
        for (let d = 0; d < 7; d++) this.markLocalEdit(d)
        this.$set(this.data, 'events', this.data.events.slice())
        this.scheduleSave()
        this.refreshOwnActivity()
      }
    },
    /**
     * 定位某天里的一条时间块。
     *
     * ⚠️ **必须优先按 key 找，不能只信 index**：一次「修改」如果撞上时间冲突，
     *    TimeTable 会先把被盖住的旧记录删掉/缩短，数组长度一变，
     *    弹窗里早先记下来的 index 就指向**另一条**记录了 ——
     *    于是「改了半天没变化，还多出一个块」、「点了删除却删掉另一块」，
     *    刷新页面后又回到旧数据（因为后端那一条根本没被动过）。
     *    key 不受数组增删影响，是唯一可靠的定位方式；index 只在 key 缺失时兜底。
     */
    resolveEventIndex(dayIdx, index, key) {
      const events = this.data.events[dayIdx] || []
      if (key != null && key !== '') {
        const i = events.findIndex(evt => evt && String(evt.key) === String(key))
        if (i >= 0) return i
      }
      if (typeof index === 'number' && index >= 0 && index < events.length) return index
      return -1
    },
    /**
     * 记录「用户对某天做过本地改动」，供 applyWeekData 决定哪些天需要按 id 合并、
     * 而不是直接被服务端/缓存数据整段覆盖（否则刚创建/修改的块会被冲掉）。
     */
    markLocalEdit(dayIdx) {
      const wk = this.weekKey(this.weekStart)
      if (!this.weekLocalEdits[wk]) {
        this.weekLocalEdits = { ...this.weekLocalEdits, [wk]: new Set() }
      }
      this.weekLocalEdits[wk].add(dayIdx)
      // 标记「本地刚改过」：下一轮版本轮询检测到变化时应跳过整表静默重拉（本地已有最新数据），
      // 仅轻量刷新底部动态行（见 checkVersion）。
      this.justEdited = true
    },
    onAddEvent({ dayIdx, start, end, cat, text, id, kind, plannedStartTime, plannedEndTime, actualStartTime, actualEndTime, timerAt }) {
      const events = this.data.events[dayIdx] || []
      const evt = { start, end, cat, text, key: nextBlockKey() }
      if (id) {
        evt.id = id
      }
      if (kind) {
        evt.kind = kind
      }
      // 原始时间戳：与「加载出来的块」保持同构 —— 之后「在做 → 结束」要靠 actualStartTime
      // 拼出结束时刻再重新派生，缺了会让本地派生失败（块直接塌成 0-0 消失）
      if (plannedStartTime) evt.plannedStartTime = plannedStartTime
      if (plannedEndTime) evt.plannedEndTime = plannedEndTime
      if (actualStartTime) evt.actualStartTime = actualStartTime
      if (actualEndTime) evt.actualEndTime = actualEndTime
      events.push(evt)
      this.$set(this.data.events, dayIdx, events)
      this.markLocalEdit(dayIdx)
      this.scheduleSave()
      // 「在做」开了定时 → 立刻把定时挂到全局定时器上（按后端 id 索引，刷新后仍有效）
      this.registerRunningTimer(dayIdx, evt, timerAt)
      // 新增「在做」块：立即刷新左侧实时状态卡片，让"在做"状态马上出现
      if (kind === 'running') this.refreshOwnActivity()
    },
    onDeleteEvent({ dayIdx, index, key }) {
      const events = this.data.events[dayIdx] || []
      const i = this.resolveEventIndex(dayIdx, index, key)
      if (i < 0) {
        console.warn('[ListPage] 删除时间块失败：找不到对应记录', { dayIdx, index, key })
        return
      }
      const removed = events[i]
      // 删除的可能是挂着定时的「在做」块：块没了定时必须跟着取消，
      // 否则到点会弹一个查无此块的提醒，用户点「结束」也打不到任何东西。
      if (removed && removed.id) cancelTimer(removed.id)
      events.splice(i, 1)
      this.$set(this.data.events, dayIdx, events)
      this.markLocalEdit(dayIdx)
      this.scheduleSave()
      // 删掉的若是「在做」块：立即刷新左侧实时状态卡片，避免"在做"状态残留
      if (removed && removed.kind === 'running') this.refreshOwnActivity()
    },
    onUpdateEvent({ dayIdx, index, key, start, end, cat, text, id, kind, timerAt }) {
      const events = this.data.events[dayIdx] || []
      const i = this.resolveEventIndex(dayIdx, index, key)
      if (i < 0) {
        console.warn('[ListPage] 修改时间块失败：找不到对应记录', { dayIdx, index, key })
        return
      }
      const existing = events[i]
      const evt = {
        start,
        end,
        cat,
        text,
        // key 必须原样保留：它是这条记录在数组里的"身份证"，换了就等于新增一条
        key: existing.key || nextBlockKey(),
        // 时长沿用原值（跨午夜的记录 end 会被收敛到 1440，end-start 不是真实时长）；
        // 时间被改动过、或原本就没有 duration 时，按当天分钟坐标重算
        duration: (existing.duration && existing.start === start && existing.end === end)
          ? existing.duration
          : Math.max(0, end - start),
        // 保留原有状态：编辑弹窗里类型固定为当前 kind，不会跨状态切换
        kind: existing.kind || kind || 'done'
      }
      // 原始时间戳一并保留：状态流转（预设→完成 / 正在做→结束）后需要据此重新派生，
      // 丢了会导致之后点「结束 / 勾选完成」时取不到 actual，派生失败。
      if (existing.actualStartTime) evt.actualStartTime = existing.actualStartTime
      if (existing.actualEndTime) evt.actualEndTime = existing.actualEndTime
      if (existing.plannedStartTime) evt.plannedStartTime = existing.plannedStartTime
      if (existing.plannedEndTime) evt.plannedEndTime = existing.plannedEndTime
      // ⚠️ id 必须保住：丢了它，之后对这条记录的修改/删除就都打不到后端了
      if (id || existing.id) {
        evt.id = id || existing.id
      }
      if (existing.color) {
        evt.color = existing.color
      }
      events.splice(i, 1, evt)
      this.$set(this.data.events, dayIdx, events)
      this.markLocalEdit(dayIdx)
      this.scheduleSave()
      // 「在做」的定时也要跟着编辑同步：新设 / 改时刻 → 重排；用户把开关关了 → 取消。
      // 少了这一段，编辑已有的在做块时定时完全不生效（只有新建才会 registerRunningTimer）。
      if (evt.kind === 'running' && evt.id) {
        if (timerAt == null) {
          cancelTimer(evt.id)
        } else {
          this.registerRunningTimer(dayIdx, evt, timerAt)
        }
      }
      // 「在做」块的文字 / 定时被编辑：立即刷新左侧实时状态卡片，让卡片内容同步
      if (existing.kind === 'running' || evt.kind === 'running') this.refreshOwnActivity()
    },
    /**
     * 时间块状态流转（预设→完成 / 正在做→结束）后，按新状态重新派生本地渲染字段。
     *
     * 由 TimeTable 调后端 updateTimeBlockStatus 成功后 emit 过来；
     * 这里只动本地数据，不重复发请求。kind 完全由 actual 时间戳派生，
     * 所以只要把新的 actualStartTime/actualEndTime 写回，再跑一遍 deriveEventFields 即可。
     */
    onUpdateStatus({ dayIdx, key, actualStartTime, actualEndTime }) {
      const events = this.data.events[dayIdx] || []
      const i = this.resolveEventIndex(dayIdx, -1, key)
      if (i < 0) {
        console.warn('[ListPage] 更新时间块状态失败：找不到对应记录', { dayIdx, key })
        return
      }
      const existing = events[i]
      const updated = {
        ...existing,
        actualStartTime: actualStartTime || existing.actualStartTime || null,
        actualEndTime: actualEndTime || existing.actualEndTime || null
      }
      Object.assign(updated, this.deriveEventFields(updated))
      events.splice(i, 1, updated)
      this.$set(this.data.events, dayIdx, events)
      this.markLocalEdit(dayIdx)
      this.scheduleSave()
      // 状态流转后不再是「在做」→ 定时必须取消，否则会为一个已经结束的块弹提醒。
      // planned→done 的情况：kind 仍不是 done 的语义下才 cancel，这里按实际派生结果判断。
      if (updated.kind !== 'running' && existing.id) {
        cancelTimer(existing.id)
      }
      // 状态流转（含「结束在做」）会改写自己的实时状态，立即回源刷新左侧实时状态卡片，
      // 否则要等 20s 版本轮询才变，看着像"卡在做状态"。
      this.refreshOwnActivity()
    },
    /** 第 dayIdx 天 0 点的毫秒时间戳（把「当天分钟坐标」换算成绝对时刻用） */
    dayTimestamp(dayIdx) {
      const day = this.data.days[dayIdx]
      if (!day || !day.date) return 0
      const [y, M, d] = day.date.split('-').map(Number)
      return new Date(y, M - 1, d, 0, 0, 0, 0).getTime()
    },
    /**
     * 给新建的「在做」块挂定时提醒。
     *
     * <p>`timerAt` 是 QuickAddModal 送来的**当天分钟坐标**（dayIdx 那一天的 0 点起算的分钟数），
     * 必须先换算成绝对时刻；块本身可能不是今天建的（比如补记早上的一段），
     * 换算完已经过期的直接放弃 —— 弹一个"立刻响"的提醒没有意义。</p>
     *
     * <p>只按**后端 id** 索引：`evt.key` 每次加载都会重生成（见 constants.nextBlockKey），
     * 拿它当 key 会在刷新后失联，用户点「结束」时也打不到任何块。</p>
     *
     * @param {number} dayIdx 块所在天的下标（调用方 onAddEvent 已拿到，直接传比反查稳）
     * @param {Object} evt 已插入本地的新块
     * @param {number|null} timerAt 提醒时刻（当天分钟坐标）
     */
    registerRunningTimer(dayIdx, evt, timerAt) {
      if (evt.kind !== 'running' || !evt.id) return
      if (timerAt == null) return
      const at = this.dayTimestamp(dayIdx) + Number(timerAt) * 60000
      // 已经过期的（跨天 / 补记过去的某天）→ 不起定时，弹一个马上响的提醒只会打扰人
      if (!(at > Date.now())) return
      const cat = (this.allCategories || []).find(c => c.key === String(evt.cat))
      scheduleTimer({
        id: String(evt.id),
        at,
        label: minutesToLabel(Number(timerAt)),
        // 当天分钟坐标：QuickAddModal 编辑已有的「在做」块时靠它把定时回填到开关上
        minute: Number(timerAt),
        title: evt.text || '',
        catLabel: cat ? cat.label : '',
        catColor: cat ? cat.color : '',
        startedAt: this.isoToMs(evt.actualStartTime) || at - 60000
      })
    },
    /** ISO 字符串 → 毫秒，非法输入返回 0 */
    isoToMs(iso) {
      if (!iso) return 0
      const t = new Date(iso).getTime()
      return isNaN(t) ? 0 : t
    },
    /**
     * 「在做」的定时弹窗里点了「结束」：TaskBlockPage 已调后端把状态改成 COMPLETED，
     * 这里通过 window 事件把结果同步回本地表格 —— 不整周重拉，只改那一条。
     *
     * <p>为什么要走事件：提醒弹窗挂在 .frame 根下（TaskBlockPage），用户在随想 / 朋友 /
     * 个人中心等子页也能点「结束」，那时 ListPage 可能根本没挂载。</p>
     */
    onRunningEnded(event) {
      const detail = (event && event.detail) || {}
      const id = detail.id
      if (id == null) return
      const days = this.data.events || []
      for (let dayIdx = 0; dayIdx < days.length; dayIdx++) {
        const events = days[dayIdx] || []
        const i = events.findIndex(evt => evt && String(evt.id) === String(id))
        if (i < 0) continue
        const existing = events[i]
        const isoEnd = detail.actualEndTime || this.buildIsoNow()
        const updated = {
          ...existing,
          actualEndTime: isoEnd,
          // 兜底：本地新建的块理论上带 actualStartTime，真丢了也不能让派生得到 0-0 的坏坐标
          actualStartTime: existing.actualStartTime || this.buildIsoFromMinutes(existing.start)
        }
        Object.assign(updated, this.deriveEventFields(updated))
        events.splice(i, 1, updated)
        this.$set(this.data.events, dayIdx, events)
        this.markLocalEdit(dayIdx)
        this.scheduleSave()
        // 块已经不是「在做」了：定时取消，否则到点还会为它弹一次提醒
        cancelTimer(id)
        // 定时弹窗「结束」同样改写自己的实时状态，立即刷新左侧实时状态卡片
        this.refreshOwnActivity()
        return
      }
    },
    /**
     * 「在做」跨天结束（在 TaskBlockPage 的定时弹窗里点的结束）时，第二天那块同步到本地。
     * 只补目标日期就在本周列里的块；不在本周（例如周末开始、周一结束且周一属于下一周）就不补，
     * 用户翻过去时自然会从服务端拉到。
     */
    onRunningSplit(event) {
      const blocks = (event && event.detail && event.detail.blocks) || []
      blocks.forEach(b => {
        if (!b || !b.id) return
        const dayIdx = (this.data.days || []).findIndex(d => String(d.date) === b.dateStr)
        if (dayIdx < 0) return
        // 幂等：刷新页面后 ListPage 重挂可能会收到重放事件，按 id 去重
        const events = this.data.events[dayIdx] || []
        if (events.some(evt => evt && String(evt.id) === String(b.id))) return
        const evt = {
          id: b.id,
          key: nextBlockKey(),
          cat: 'none',
          text: b.content || '',
          color: b.color || '',
          start: b.start,
          end: b.end,
          kind: 'done',
          plannedStartTime: b.plannedStartTime,
          plannedEndTime: b.plannedEndTime,
          actualStartTime: b.actualStartTime,
          actualEndTime: b.actualEndTime,
          duration: Math.max(0, b.end - b.start)
        }
        const list = events.slice()
        list.push(evt)
        // 保持按开始时间排序，渲染层就不用再排了
        list.sort((x, y) => x.start - y.start)
        this.$set(this.data.events, dayIdx, list)
        this.markLocalEdit(dayIdx)
        this.scheduleSave()
      })
    },
    /** 当前时刻的本地 ISO 串（deriveEventFields / 后端吃的格式：yyyy-MM-ddTHH:mm:ss） */
    buildIsoNow() {
      const d = new Date()
      const p = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
    },
    /** 用「当天分钟坐标」拼一个本地 ISO 串（日期取今天：块在本页可见即属于当前周） */
    buildIsoFromMinutes(minutes) {
      const now = new Date()
      const p = n => String(n).padStart(2, '0')
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
      d.setMinutes(d.getMinutes() + Number(minutes || 0))
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:00`
    },
    onChangeWeek(offsetDays) {
      const d = new Date(this.weekStart)
      d.setDate(d.getDate() + offsetDays)
      this.weekStart = d
    },
    onGoToday() {
      this.weekStart = getMonday(new Date())
    },
    onJumpToDate(dateStr) {
      const [y, M, d] = dateStr.split('-').map(Number)
      this.weekStart = getMonday(new Date(y, M - 1, d))
    },
    toggleSetting() {
      this.settingOpen = !this.settingOpen
    },
    onDownloadTable() {
      this.$refs.timeTable && this.$refs.timeTable.downloadTable()
    },
    /**
     * 手动刷新：整周数据 + 分类 + 未读角标一起重拉。
     *
     * loadWeek 会把本周的 weekLocalEdits 清空，因此刷新后是「以服务端为准」的整段替换
     * （本地改动此前已通过 scheduleSave 落库，不会被丢）。
     * 三个请求并发，任一失败由各自内部兜底（loadWeek 回退本地缓存、通知静默），
     * 所以这里只需保证 refreshing 状态一定复位。
     */
    async onRefresh() {
      if (this.refreshing) return
      this.refreshing = true
      try {
        await Promise.all([
          this.loadWeek(this.weekStart),
          this.loadTags(),
          this.refreshNotificationCounts()
        ])
      } finally {
        this.refreshing = false
      }
    },
    onToggleFullscreen() {
      this.$refs.timeTable && this.$refs.timeTable.toggleFullscreen()
    },
    /** 打开「开放时间表」弹窗 */
    onOpenShare() {
      if (!this.projectId) {
        this.$message && this.$message.warning('请先选择工作表')
        return
      }
      this.shareVisible = true
    },
    /** 打开评论 / 点赞 / 分享 提醒面板 */
    onOpenNotifications(type) {
      this.notifType = (type === 'LIKE' || type === 'SHARE') ? type : 'COMMENT'
      this.notifVisible = true
    },
    /**
     * 拉取互动通知，汇总各类型未读数驱动角标。
     * 评论 / 点赞 / 分享 是全局的（属于当前登录用户），与 projectId 无关，
     * 进入页面与切换工作表时各刷新一次即可。
     */
    async refreshNotificationCounts() {
      try {
        const res = await listNotifications(50)
        if (!res.success || res.code !== 200) return
        const counts = (res.data && res.data.unreadCounts) || {}
        this.commentUnread = Number(counts.COMMENT) || 0
        this.likeUnread = Number(counts.LIKE) || 0
        this.shareUnread = Number(counts.SHARE) || 0
      } catch (e) {
        // 通知拉取失败不应影响主流程（看表），静默忽略即可
        console.warn('[ListPage] 拉取互动通知失败', e)
      }
    },

    // ===== 时间块评论气泡（悬停时间块浮出） =====
    /**
     * 悬停进入某个时间块：起一个 0.5s 的停顿计时才弹气泡（掠过不弹）。
     * 已在同块的不同切片间移动时不重开，只挪锚点续上自动消失计时。
     */
    onHoverEvent(payload) {
      if (!payload || !payload.event || !payload.event.id) return
      if (this.bubbleVisible && this.bubbleBlock &&
          String(this.bubbleBlock.id) === String(payload.event.id)) {
        this.bubbleAnchor = payload.rect || this.bubbleAnchor
        this.startAutoHide()
        return
      }
      this.clearTimers()
      if (this.bubbleVisible) this.resetBubble()
      const pending = payload
      this.hoverTimer = setTimeout(() => {
        this.hoverTimer = null
        this.openBubble(pending)
      }, 500)
    },
    /** 鼠标离开时间块：撤掉待弹计时；已弹出的给一点宽限，方便移进气泡。 */
    onHoverLeave() {
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer)
        this.hoverTimer = null
      }
      // 已固定（用户正在气泡里写评论）的气泡不被鼠标划过带走
      if (this.bubblePinned) return
      if (!this.bubbleVisible) return
      clearTimeout(this.leaveTimer)
      this.leaveTimer = setTimeout(() => {
        if (this.bubblePinned || this.pointerInBubble) return
        this.hideBubble()
      }, 240)
    },
    /**
     * 焦点进到气泡里（用户点进评论输入框开始写）→ **把气泡升级成固定态**。
     *
     * ⚠️ 不加这一步，鼠标一移出气泡 300ms 气泡就收起、输入框被销毁、焦点掉回 body，
     *    用户再按空格就变成浏览器翻页（页面下滑）—— 症状正是「气泡里打不了字，一按空格就退出」。
     *    固定后只有点关闭按钮 / 点气泡外面 / 切周才会收起。
     */
    onBubbleInteracting(active) {
      if (!active) return
      this.clearTimers()
      this.bubblePinned = true
    },
    openBubble(payload) {
      const evt = payload.event
      this.bubbleBlock = {
        id: evt.id,
        text: evt.text || '时间块',
        color: evt.color || '',
        timeLabel: `${minutesToLabel(evt.start)}-${minutesToLabel(evt.end)}`
      }
      this.bubbleDayIdx = payload.dayIdx != null ? payload.dayIdx : -1
      this.bubbleEventIndex = payload.index != null ? payload.index : -1
      this.bubbleEventKey = payload.event ? payload.event.key : null
      this.bubbleAnchor = payload.rect || null
      this.bubbleVisible = true
      this.startAutoHide()
    },
    startAutoHide() {
      clearTimeout(this.autoHideTimer)
      this.autoHideTimer = setTimeout(() => {
        this.autoHideTimer = null
        if (this.bubblePinned || this.pointerInBubble) return
        this.hideBubble()
      }, 4000)
    },
    /** 鼠标移入气泡：视为用户想看/想回复，取消自动消失与收起计时。 */
    onBubblePointerEnter() {
      this.pointerInBubble = true
      clearTimeout(this.autoHideTimer)
      this.autoHideTimer = null
      clearTimeout(this.leaveTimer)
      this.leaveTimer = null
    },
    /** 鼠标移出气泡：给 300ms 宽限，回不去就收起（固定态除外）。 */
    onBubblePointerLeave() {
      this.pointerInBubble = false
      if (this.bubblePinned) return
      clearTimeout(this.leaveTimer)
      this.leaveTimer = setTimeout(() => {
        if (this.bubblePinned || this.pointerInBubble) return
        this.hideBubble()
      }, 300)
    },
    clearTimers() {
      if (this.hoverTimer) { clearTimeout(this.hoverTimer); this.hoverTimer = null }
      if (this.autoHideTimer) { clearTimeout(this.autoHideTimer); this.autoHideTimer = null }
      if (this.leaveTimer) { clearTimeout(this.leaveTimer); this.leaveTimer = null }
    },
    /** 只清数据不撤计时器，用于换块时的瞬间收起 */
    resetBubble() {
      this.bubbleVisible = false
      this.bubbleBlock = null
      this.bubbleAnchor = null
      this.bubbleDayIdx = -1
      this.bubbleEventIndex = -1
      this.bubbleEventKey = null
    },
    hideBubble() {
      this.clearTimers()
      this.resetBubble()
      this.pointerInBubble = false
    },
    /**
     * 表格滚动后关闭气泡，避免源时间块滚出视野后失去上下文。
     *
     * ⚠️ 已固定的气泡（用户正在写评论）不在此列：滚一下就把人家写了半天的输入框收掉太粗暴，
     *    而且焦点一丢，紧接着的空格会变成浏览器翻页。
     */
    onTableScroll() {
      if (this.bubbleVisible && !this.bubblePinned) this.hideBubble()
    },
    /**
     * 气泡里发表评论 / 回复 / 点赞 / 取消点赞成功后，重新拉一次互动统计，
     * 让时间块右下角的角标数字跟着变（气泡自己只刷新了评论列表）。
     */
    onBubbleChanged() {
      this.loadInteractions()
      // 自己发的评论 / 点赞也会生成一条提醒（双向互动都要进提醒列表），顺带刷新红点
      this.refreshNotificationCounts()
    },
    toggleFoldable(idx) {
      const next = new Set(this.foldableRows)
      if (next.has(idx)) {
        next.delete(idx)
        this.collapsedRows.delete(idx)
      } else {
        next.add(idx)
      }
      this.foldableRows = next
    },
    toggleCollapse(idx) {
      const next = new Set(this.collapsedRows)
      if (next.has(idx)) {
        next.delete(idx)
      } else {
        next.add(idx)
      }
      this.collapsedRows = next
    }
  }
}
</script>

<style scoped>
.frame{
  width:100%;
  /* 必须用「确定高度」height:100vh，不能用 min-height。
     原因：TimeTable 的表头靠 position:sticky 固定，而 sticky 是相对「最近的滚动祖先」生效的。
     若 .frame 只有 min-height（高度不确定），整条 flex 链（.list-main → .list-card → .tablebox）
     都拿不到确定高度，.tablebox 就会撑到内容高度、自己永远不滚动 —— 于是真正滚动的是整页，
     而 sticky 相对 .tablebox（不滚动）等于失效，表头就会跟着页面一起滚走，
     表现为「行滚上来盖过/超出表头」。
     改成 height:100vh 后，flex 链拿到确定高度，.tablebox 变成真正的内部滚动容器，
     表头 sticky 才会生效。 */
  height:100vh;
  border:none;
  border-radius:0;
  background:#eef7f0;
  position:relative;
  /* 视口过矮时（内容确实放不下）允许整页滚动兜底，避免内容被裁掉 */
  overflow:auto;
  padding:1.75rem 2.75rem 2.5rem;
  display:flex;
  flex-direction:column;
  /* 轻量扁平主题令牌统一定义在 App.vue 的 :root 里（唯一来源），这里不再重复声明。
     所有引用都写成 var(--lp-x, 兜底值)，即使脱离 App.vue 单独渲染也不会掉色。 */
}
.frame > *{
  position:relative;
  z-index:2;
}

.list-main{
  flex:1;
  display:flex;
  flex-direction:column;
  margin-top:1.5rem;
  min-height:0;
}

.list-card{
  background:var(--lp-surface,#fff);
  border-radius:1rem;
  box-shadow:var(--lp-shadow-lg,0 8px 24px rgba(52,102,68,.08));
  padding:1.75rem 1.75rem 1.375rem;
  display:flex;
  flex-direction:column;
  flex:1;
  min-height:0;
}

.list-header{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  flex-wrap:wrap;
  gap:0.75rem;
  border-bottom:1px solid var(--lp-border-soft,#eef5f0);
  padding-bottom:0.875rem;
  margin-bottom:1.125rem;
}
.header-actions{
  display:flex;
  align-items:flex-start;
  gap:0.625rem;
  position:relative;
}
/* 轻量扁平：白底 + 2px 描边 + 8px 圆角，hover 只做浅绿底 */
.settings-btn{
  border:2px solid var(--lp-border,#e3efe6);
  background:var(--lp-surface,#fff);
  color:var(--lp-text,#26332b);
  padding:0.313rem 0.75rem;
  border-radius:0.5rem;
  cursor:pointer;
  font-size:0.813rem;
  font-family:inherit;
  transition:.15s;
  white-space:nowrap;
}
.settings-btn:hover{
  background:var(--lp-surface-2,#f7fbf8);
  border-color:var(--lp-primary-soft2,#d5ebdb);
  color:var(--lp-primary-dark,#2f7a4a);
}
.settings-panel{
  position:absolute;
  top:calc(100% + 0.5rem);
  right:0;
  width:220px;
  max-height:320px;
  overflow:auto;
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.75rem;
  box-shadow:var(--lp-shadow-lg,0 8px 24px rgba(52,102,68,.08));
  padding:0.75rem;
  z-index:10;
}
.settings-title{
  font-size:0.813rem;
  font-weight:600;
  color:var(--lp-primary-dark,#2f7a4a);
  margin-bottom:0.625rem;
}
.settings-list{
  display:flex;
  flex-direction:column;
  gap:0.375rem;
}
.settings-item{
  display:flex;
  align-items:center;
  gap:0.5rem;
  font-size:0.75rem;
  color:var(--lp-text-2,#5f7268);
  cursor:pointer;
}
.settings-item input{
  cursor:pointer;
}

/* 「我的xx」标题块：图标在标题左侧，尺寸/间距对齐左侧「我的工作区」面板
 * （.repo-panel .title 是 font-size:22px + gap:10px + 行内图标）。
 * 图标槽宽度与「图标↔标题」间距在这里定义一次，下方 .sub 的缩进直接引用这两个变量，
 * 避免「图标宽 32px」在缩进处再手写一份魔法数字。 */
.list-heading{
  --list-icon-slot:32px;
  --list-icon-gap:0.625rem;
  min-width:0;
}
/* 图标与 h1 是同一行的 flex 兄弟，align-items:center 让图标与标题行**严格**垂直居中。
 * （若改成「图标 + 文本列」两列结构再 flex-start 对齐，图标槽 32px 与 h1 行盒 29px
 *   高度不等，会差 1.5px，所以这里刻意保留单行 flex 结构。） */
.list-title{
  display:flex;
  align-items:center;
  gap:var(--list-icon-gap);
  min-width:0;
}
/* 工作表图标：方形槽 32×32 + 20px 字号，尺寸与左侧工作区列表的 .repo-icon 一致。
 * ⚠️ 颜色刻意**跟随标题字体色**（--lp-text），不用工作表主色 —— 用户要求与标题同色。 */
.list-title-icon{
  flex:0 0 auto;
  width:var(--list-icon-slot);
  height:var(--list-icon-slot);
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:20px;
  font-weight:bold;
  line-height:1;
  color:var(--lp-text,#26332b);
}

/* 字号与「我的工作区」标题（.repo-panel .title）保持一致。
 * ⚠️ 必须用绝对 22px，不能写 rem：App.vue 会按断点改 html 的 font-size（14/16/18/20px），
 *    而左侧标题是固定 22px 不缩放，用 rem 在 1280 以上会变成 24.75px 就对不上了。 */
h1{
  font-size:22px;
  letter-spacing:1px;
  margin:0;
  font-weight:700;
  color:var(--lp-text,#26332b);
}

h1 span{
  color:var(--lp-primary,#4aa46a);
}

.sub{
  color:var(--lp-text-3,#94a59b);
  font-size:0.813rem;
  margin-top:0.25rem;
}
/* 描述左缩进 = 图标槽宽 + 图标↔标题间距（变量定义在 .list-heading 上）→
   左边缘与标题**文字**左边缘严格对齐，而不是被左侧图标顶开。
   按约定写成原块之后的覆盖块，原块未声明 padding-left，这里不构成覆盖冲突。 */
.sub{
  padding-left:calc(var(--list-icon-slot) + var(--list-icon-gap));
}

@media (max-width:800px){
  .frame{padding:1.25rem 1.25rem 1.875rem;}
  /* h1 不再在这里降字号：要始终与左侧「我的工作区」标题（22px）保持一致 */
  .list-card{padding:1.125rem 1rem 1rem;}
}
</style>
